import { BookOpenText } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useMesProcedures } from '@/api/hooks';
import type { StatutConsultation } from '@/api/types';
import { Chargement, ErreurBloc, Kpi, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr } from '@/lib/utils';
import { CelluleStatutPersonnel } from './Bibliotheque';

const FILTRES: Array<{ cle: StatutConsultation | undefined; libelle: string }> = [
  { cle: undefined, libelle: 'Toutes' },
  { cle: 'A_CONSULTER', libelle: 'À consulter' },
  { cle: 'LU', libelle: 'Lues' },
  { cle: 'A_RELIRE', libelle: 'À relire' },
  { cle: 'CONFIRMATION_ATTENDUE', libelle: 'Confirmations attendues' },
  { cle: 'CONFIRMEE', libelle: 'Confirmées' },
];

/** Onglet « Mes procédures » de l'espace personnel (§ 6.3) : procédures applicables, statuts, première consultation, échéance, confirmation. */
export function MesProcedures() {
  const [statut, setStatut] = useState<StatutConsultation | undefined>(undefined);
  const { data, isLoading, error } = useMesProcedures(statut);
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const i = data.indicateurs;
  return (
    <div className="space-y-4" data-testid="mes-procedures">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <button type="button" className="text-left" onClick={() => setStatut('A_CONSULTER')}>
          <Kpi label="À consulter" valeur={i.aConsulter} accent={i.aConsulter > 0 ? 'alerte' : 'ok'} detail="jamais ouvertes dans le lecteur" />
        </button>
        <button type="button" className="text-left" onClick={() => setStatut('LU')}>
          <Kpi label="Lues" valeur={i.lues} detail="version en vigueur consultée" />
        </button>
        <button type="button" className="text-left" onClick={() => setStatut('A_RELIRE')}>
          <Kpi label="Nouvelles versions" valeur={i.nouvellesVersions} accent={i.nouvellesVersions > 0 ? 'alerte' : undefined} detail="à relire" />
        </button>
        <button type="button" className="text-left" onClick={() => setStatut('CONFIRMATION_ATTENDUE')}>
          <Kpi label="Confirmations attendues" valeur={i.confirmationsAttendues} accent={i.confirmationsAttendues > 0 ? 'alerte' : undefined} detail="prise de connaissance à confirmer" />
        </button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <BookOpenText className="size-5" /> Mes procédures
            </span>
            <Button asChild variant="outline" size="sm">
              <Link to="/procedures">Ouvrir la bibliothèque</Link>
            </Button>
          </CardTitle>
          <CardDescription>Procédures applicables à votre fonction, votre direction, vos profils ou à tout le Fonds. Le statut « Lu » est une trace de consultation dans le lecteur intégré, enregistrée sous votre compte.</CardDescription>
          <div className="flex flex-wrap gap-1 pt-2">
            {FILTRES.map((f) => (
              <Button key={f.libelle} type="button" size="sm" variant={statut === f.cle ? 'default' : 'outline'} className={cn('h-7')} onClick={() => setStatut(f.cle)}>
                {f.libelle}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {!data.procedures.length ? (
            <Vide message={statut ? 'Aucune procédure dans cet état.' : 'Aucune procédure ne vous est applicable pour le moment.'} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Procédure</TableHead>
                  <TableHead>Domaine</TableHead>
                  <TableHead>Applicabilité</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Première consultation</TableHead>
                  <TableHead>Échéance</TableHead>
                  <TableHead>Confirmation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.procedures.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Link to={`/procedures/${p.id}`} className="font-medium text-primary hover:underline">
                        {p.reference}
                      </Link>
                      <div className="text-sm">{p.titre}</div>
                    </TableCell>
                    <TableCell className="text-sm">{p.domaine.libelle}</TableCell>
                    <TableCell>
                      <Badge variant={p.commune ? 'secondary' : 'outline'}>{p.etiquette}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{p.versionEnVigueur ? `v${p.versionEnVigueur.numero} · ${dateFr(p.versionEnVigueur.datePublication)}` : '—'}</TableCell>
                    <TableCell>
                      <CelluleStatutPersonnel p={p} />
                    </TableCell>
                    <TableCell className="text-sm">{p.personnel?.premiereConsultation ? dateFr(p.personnel.premiereConsultation) : '—'}</TableCell>
                    <TableCell className={cn('text-sm', p.personnel?.enRetard && 'font-medium text-red-700')}>{p.personnel?.echeance ? dateFr(p.personnel.echeance) : '—'}</TableCell>
                    <TableCell className="text-sm">{p.confirmationRequise ? (p.personnel?.confirmeeLe ? `confirmée le ${dateFr(p.personnel.confirmeeLe)}` : 'attendue') : 'non requise'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
